import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, WifiOff, ListTodo, Route } from 'lucide-react';
import { Header } from '@/components/Header';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SortMenu, type SortMode, type PriorityMode } from '@/components/SortMenu';
import { OwnershipFilter } from '@/components/OwnershipFilter';
import { useAuth } from '@/auth';
import { TaskDetailSheet } from '@/features/tasks/TaskDetailSheet';
import type { HeaderMenuItem } from '@/components/Header';
import {
  useCompleteTask,
  useUpdateTask,
  useCancelTask,
  useOutdoorTasksForVisits,
} from '@/features/tasks/hooks/useTasks';
import type { Task, UpdateTaskDto } from '@/api/tasks';
import type { OwnershipFilterValue } from '@/api/visits';
import { VisitList } from '@/features/visits/VisitList';
import { VisitContextMenu } from '@/features/visits/VisitContextMenu';
import { ScheduleVisitDialog } from '@/features/visits/ScheduleVisitDialog';
import { ActiveVisitBanner } from '@/features/visits/ActiveVisitBanner';
import { useWeeklyVisits, useSetPriority, useCancelVisit, usePlanToday, useCreateVisit, useMarkSkipped } from '@/features/visits/hooks/useWeeklyVisits';
import { useClientIndicators } from '@/features/visits/hooks/useClientIndicators';
import {
  useActiveVisit,
  useCheckOut,
} from '@/features/visits/hooks/useActiveVisit';
import {
  useVisitsSorting,
  type SortedVisitItem,
} from '@/features/visits/hooks/useVisitsSorting';
import { ISPStatusSheet } from '@/features/isp/ISPStatusSheet';
import { useISPStatus } from '@/features/isp/hooks/useISPStatus';
import { usePaidOrders } from '@/features/waybills';

/**
 * Calculate ISO week code (YYYY-Wxx) for a date offset by weeks
 */
function getWeekCodeWithOffset(weekOffset: number): string | undefined {
  if (weekOffset === 0) return undefined; // Let backend use current week

  const date = new Date();
  date.setDate(date.getDate() + weekOffset * 7);

  // ISO week calculation
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);

  return `${d.getUTCFullYear()}-W${weekNo.toString().padStart(2, '0')}`;
}

type FilterValue = OwnershipFilterValue[] | 'all';

const OWNERSHIP_FILTER_KEY = 'visits-ownership-filter';
const SHOW_CLIENT_TASKS_KEY = 'visits-show-client-tasks';
const SORT_MODE_KEY = 'visits-sort-mode';
const PRIORITY_MODE_KEY = 'visits-priority-mode';

const VALID_SORT_MODES: SortMode[] = ['fromMe', 'fromLast', 'shortest', 'name', 'class'];

function loadOwnershipFilter(): FilterValue {
  try {
    const saved = localStorage.getItem(OWNERSHIP_FILTER_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed === 'all' || (Array.isArray(parsed) && parsed.length > 0)) {
        return parsed;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return ['own'];
}

function loadSortMode(): SortMode {
  try {
    const saved = localStorage.getItem(SORT_MODE_KEY);
    if (saved && VALID_SORT_MODES.includes(saved as SortMode)) {
      return saved as SortMode;
    }
  } catch {
    // Ignore errors
  }
  return 'fromLast';
}

const VALID_PRIORITY_MODES: PriorityMode[] = ['none', 'priority', 'priorityGps'];

function loadPriorityMode(): PriorityMode {
  try {
    const saved = localStorage.getItem(PRIORITY_MODE_KEY);
    if (saved && VALID_PRIORITY_MODES.includes(saved as PriorityMode)) {
      return saved as PriorityMode;
    }
  } catch {
    // Ignore errors
  }
  return 'priority'; // default: priority enabled
}

export function VisitsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sortMode, setSortModeState] = useState<SortMode>(loadSortMode);
  const [priorityMode, setPriorityModeState] = useState<PriorityMode>(loadPriorityMode);
  const [ownershipFilters, setOwnershipFilters] = useState<FilterValue>(loadOwnershipFilter);

  // Persist sort settings to localStorage
  const setSortMode = useCallback((mode: SortMode) => {
    setSortModeState(mode);
    localStorage.setItem(SORT_MODE_KEY, mode);
  }, []);

  const setPriorityMode = useCallback((value: PriorityMode) => {
    setPriorityModeState(value);
    localStorage.setItem(PRIORITY_MODE_KEY, value);
  }, []);
  const [weekOffset, setWeekOffset] = useState(0);
  const [ispPanelOpen, setIspPanelOpen] = useState(false);
  const [showClientTasks, setShowClientTasks] = useState(() => {
    return localStorage.getItem(SHOW_CLIENT_TASKS_KEY) === 'true';
  });
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ item: SortedVisitItem; position: { x: number; y: number } } | null>(null);
  const [scheduleTarget, setScheduleTarget] = useState<{ visitTargetId: number; clientId: number; clientName?: string } | null>(null);

  // ISP status for indicator badge
  const { data: ispData } = useISPStatus();
  const ispDownCount = ispData?.totalDown || 0;

  // Edge swipe from right edge to open ISP panel
  const edgeSwipeStart = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [swipeProgress, setSwipeProgress] = useState(0); // 0-100%

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      // Start tracking if touch begins within 30px of right edge
      if (window.innerWidth - touch.clientX < 30) {
        edgeSwipeStart.current = touch.clientX;
        setSwipeProgress(5); // Show indicator immediately
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (edgeSwipeStart.current !== null) {
        const currentX = e.touches[0].clientX;
        const delta = edgeSwipeStart.current - currentX;
        // Progress: 0-100% over 80px of movement
        const progress = Math.min(100, Math.max(0, (delta / 80) * 100));
        setSwipeProgress(progress);
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (edgeSwipeStart.current !== null) {
        const endX = e.changedTouches[0].clientX;
        // Open panel if swiped left by at least 50px (62.5% progress)
        if (edgeSwipeStart.current - endX > 50) {
          setIspPanelOpen(true);
        }
        edgeSwipeStart.current = null;
        setSwipeProgress(0);
      }
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  // Calculate weekCode from offset
  const weekCode = useMemo(() => getWeekCodeWithOffset(weekOffset), [weekOffset]);

  // Persist filter to localStorage
  const handleOwnershipChange = (value: FilterValue) => {
    setOwnershipFilters(value);
    localStorage.setItem(OWNERSHIP_FILTER_KEY, JSON.stringify(value));
  };
  const [search, setSearch] = useState('');

  // Convert ownership filters to API string format
  const ownershipFilterStr = useMemo(() => {
    if (ownershipFilters === 'all') return 'all';
    return ownershipFilters.join(',');
  }, [ownershipFilters]);

  // Data fetching
  const {
    data: weeklyData,
    isLoading,
    isRefetching,
  } = useWeeklyVisits(ownershipFilters, weekCode);
  const { data: activeVisit } = useActiveVisit();

  // Pass visits through - main filtering happens in pending/completed with indicators
  const filteredVisits = useMemo(() => {
    return weeklyData?.visits;
  }, [weeklyData?.visits]);

  // Fetch outdoor tasks once for all clients (only when showClientTasks is enabled)
  const { data: outdoorTasksData } = useOutdoorTasksForVisits(
    ownershipFilterStr,
    showClientTasks
  );

  // Mutations (only checkout needed for banner)
  const checkOut = useCheckOut();

  // Visit action mutations (for context menu)
  const setPriority = useSetPriority();
  const cancelVisit = useCancelVisit();
  const planToday = usePlanToday();
  const createVisit = useCreateVisit();
  const markSkipped = useMarkSkipped();

  // Task mutations
  const completeTask = useCompleteTask();
  const updateTask = useUpdateTask();
  const cancelTask = useCancelTask();

  // Process visits with OSRM-based sorting
  const {
    pendingVisits,
    completedVisits,
    isCalculating,
  } = useVisitsSorting({
    visits: filteredVisits,
    sortMode,
    priorityMode,
    activeVisitTargetId: activeVisit?.visitTargetId,
  });

  // Fetch client indicators (for search and display)
  const clientIds = useMemo(
    () => [...pendingVisits, ...completedVisits].map((v) => v.client.id),
    [pendingVisits, completedVisits]
  );
  const { data: indicatorsData } = useClientIndicators(clientIds);

  // Fetch waybills for visible clients (for indicator icon)
  const clientNames = useMemo(() => {
    const names = new Set<string>();
    for (const v of [...pendingVisits, ...completedVisits]) {
      if (v.client?.name) {
        names.add(v.client.name);
      }
    }
    return Array.from(names);
  }, [pendingVisits, completedVisits]);
  const { data: waybillsData } = usePaidOrders(clientNames, false);
  const clientsWithWaybills = useMemo(() => {
    const set = new Set<string>();
    if (waybillsData) {
      for (const order of waybillsData) {
        set.add(order.company);
      }
    }
    return set;
  }, [waybillsData]);

  // Current user abbreviation for filtering
  const currentAbbr = user?.email?.split('@')[0] || null;

  // Filter main offices: show only if user has real relation
  // (responsible, delegated, or subordinate's client)
  const pending = useMemo(() => {
    let result = pendingVisits;

    // Filter main offices (not for 'all' filter)
    if (ownershipFilters !== 'all' && indicatorsData) {
      const delegatedSet = new Set(weeklyData?.delegatedClientIds || []);

      result = result.filter((v) => {
        // Always show additional offices
        if (v.isAdditionalOffice) return true;

        // For main offices: check real ownership
        const clientId = v.client.id;
        const respAbbr = indicatorsData.indicators[clientId]?.responsibleEmployee?.abbr;

        // 1. User is responsible for main office
        if (respAbbr === currentAbbr) return true;

        // 2. Client is delegated to user
        if (delegatedSet.has(clientId)) return true;

        // 3. Client belongs to subordinate
        if (v.ownershipTypes.includes('subordinate')) return true;

        // 4. Otherwise - user only has additionalOffice relation, hide main office
        return false;
      });
    }

    // Then apply search filter
    if (!search) return result;
    const q = search.toLowerCase();
    return result.filter((v) => {
      const indicators = indicatorsData?.indicators[v.client.id];
      const emp = indicators?.responsibleEmployee;
      return (
        (v.displayName || v.client.name).toLowerCase().includes(q) ||
        emp?.abbr?.toLowerCase().includes(q) ||
        emp?.lastName?.toLowerCase().includes(q) ||
        emp?.firstName?.toLowerCase().includes(q)
      );
    });
  }, [pendingVisits, search, indicatorsData, ownershipFilters, currentAbbr, weeklyData?.delegatedClientIds]);

  const completed = useMemo(() => {
    let result = completedVisits;

    // Same filter for completed visits
    if (ownershipFilters !== 'all' && indicatorsData) {
      const delegatedSet = new Set(weeklyData?.delegatedClientIds || []);

      result = result.filter((v) => {
        if (v.isAdditionalOffice) return true;

        const clientId = v.client.id;
        const respAbbr = indicatorsData.indicators[clientId]?.responsibleEmployee?.abbr;

        if (respAbbr === currentAbbr) return true;
        if (delegatedSet.has(clientId)) return true;
        if (v.ownershipTypes.includes('subordinate')) return true;

        return false;
      });
    }

    if (!search) return result;
    const q = search.toLowerCase();
    return result.filter((v) => {
      const indicators = indicatorsData?.indicators[v.client.id];
      const emp = indicators?.responsibleEmployee;
      return (
        (v.displayName || v.client.name).toLowerCase().includes(q) ||
        emp?.abbr?.toLowerCase().includes(q) ||
        emp?.lastName?.toLowerCase().includes(q) ||
        emp?.firstName?.toLowerCase().includes(q)
      );
    });
  }, [completedVisits, search, indicatorsData, ownershipFilters, currentAbbr, weeklyData?.delegatedClientIds]);

  // Navigate to client page on click (uses visitTargetId for offices)
  const handleItemClick = useCallback((item: SortedVisitItem) => {
    navigate(`/visits/client/${item.visitTargetId}`, {
      state: {
        clientId: item.client.id,
        officeName: item.isAdditionalOffice ? item.office?.name : undefined,
        officeId: item.isAdditionalOffice ? item.office?.id : undefined,
      },
    });
  }, [navigate]);

  const handleCheckOut = useCallback(
    (note?: string) => {
      checkOut.mutate(note ? { note } : undefined);
    },
    [checkOut]
  );

  const handleShowClientTasksChange = useCallback((checked: boolean) => {
    setShowClientTasks(checked);
    localStorage.setItem(SHOW_CLIENT_TASKS_KEY, String(checked));
  }, []);

  const handleShowRoute = useCallback(() => {
    navigate('/visits/route', {
      state: { visits: pendingVisits },
    });
  }, [navigate, pendingVisits]);

  const headerMenuItems: HeaderMenuItem[] = useMemo(() => [
    {
      type: 'action',
      label: 'Показать маршрут',
      icon: <Route className="h-4 w-4 mr-2" />,
      onClick: handleShowRoute,
    },
    {
      type: 'checkbox',
      label: 'Показывать задачи',
      icon: <ListTodo className="h-4 w-4 mr-2" />,
      checked: showClientTasks,
      onCheckedChange: handleShowClientTasksChange,
    },
  ], [showClientTasks, handleShowClientTasksChange, handleShowRoute]);

  const handleTaskSelect = useCallback((task: Task) => {
    setSelectedTask(task);
  }, []);

  const handleTaskComplete = useCallback((id: string) => {
    completeTask.mutate(id);
  }, [completeTask]);

  const handleTaskUpdate = useCallback((id: string, updates: UpdateTaskDto) => {
    updateTask.mutate({ id, updates });
  }, [updateTask]);

  const handleTaskCancel = useCallback((id: string) => {
    cancelTask.mutate(id);
  }, [cancelTask]);

  // Long-press handler for context menu
  const handleLongPress = useCallback((item: SortedVisitItem, pos: { x: number; y: number }) => {
    // Don't show menu for active (checked-in) visits
    if (item.visitTargetId === activeVisit?.visitTargetId) return;
    setContextMenu({ item, position: pos });
  }, [activeVisit?.visitTargetId]);

  const handleContextMenuClose = useCallback(() => {
    setContextMenu(null);
  }, []);

  return (
    <div ref={containerRef} className="flex flex-col min-h-full">
      {/* Edge swipe indicator */}
      {swipeProgress > 0 && (
        <div
          className="fixed right-0 top-0 bottom-0 z-50 bg-primary/20 transition-[width] duration-75"
          style={{ width: `${Math.min(swipeProgress * 0.8, 60)}px` }}
        >
          <div
            className="absolute left-0 top-1/2 -translate-y-1/2 w-1 bg-primary rounded-full transition-all duration-75"
            style={{
              height: `${20 + swipeProgress * 0.6}%`,
              opacity: Math.min(1, swipeProgress / 50),
            }}
          />
        </div>
      )}

      {/* Header */}
      <Header
        title="Визиты"
        subtitle={
          <div className="flex items-center justify-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 p-0"
              onClick={() => setWeekOffset((prev) => prev - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span
              className={weekOffset === 0 ? '' : 'cursor-pointer underline underline-offset-2'}
              onClick={() => weekOffset !== 0 && setWeekOffset(0)}
            >
              {weeklyData?.weekRange || '...'}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 p-0"
              onClick={() => setWeekOffset((prev) => prev + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        }
        rightContent={
          <div className="flex items-center gap-2">
            {/* ISP Status button with badge */}
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              onClick={() => setIspPanelOpen(true)}
            >
              <WifiOff className={`h-5 w-5 ${ispDownCount > 0 ? 'text-destructive' : 'text-muted-foreground'}`} />
              {ispDownCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
                  {ispDownCount > 99 ? '99+' : ispDownCount}
                </span>
              )}
            </Button>
          </div>
        }
        menuItems={headerMenuItems}
      />

      {/* Active visit banner */}
      {activeVisit?.client && (
        <ActiveVisitBanner
          visit={activeVisit}
          onCheckOut={handleCheckOut}
          isCheckingOut={checkOut.isPending}
        />
      )}

      {/* Search bar + filters */}
      <div className="px-4 py-2 border-b flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Поиск клиента..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <OwnershipFilter value={ownershipFilters} onChange={handleOwnershipChange} />
        <SortMenu
          value={sortMode}
          onChange={setSortMode}
          priorityMode={priorityMode}
          onPriorityModeChange={setPriorityMode}
        />
      </div>

      {/* Visit list */}
      <div className="flex-1 overflow-y-auto">
        <VisitList
          items={pending}
          completedItems={completed}
          isLoading={isLoading}
          isRefetching={isRefetching || isCalculating}
          onItemClick={handleItemClick}
          activeVisitTargetId={activeVisit?.visitTargetId}
          showClientTasks={showClientTasks}
          tasksByClientId={outdoorTasksData?.byClientId}
          onTaskSelect={handleTaskSelect}
          indicatorsData={indicatorsData}
          clientsWithWaybills={clientsWithWaybills}
          onLongPress={handleLongPress}
          highlightedItemId={contextMenu?.item.visitTargetId}
        />
      </div>

      {/* ISP Status Panel (swipe left or click button to open) */}
      <ISPStatusSheet
        open={ispPanelOpen}
        onOpenChange={setIspPanelOpen}
      />

      {/* Visit Context Menu */}
      {contextMenu && (
        <VisitContextMenu
          item={contextMenu.item}
          position={contextMenu.position}
          onClose={handleContextMenuClose}
          onSetUrgent={(id) => setPriority.mutate({ visitTargetId: id, priority: 'URGENT' })}
          onRemoveUrgent={(id) => cancelVisit.mutate(id)}
          onPlanToday={(id) => planToday.mutate(id)}
          onCancelVisit={(id) => cancelVisit.mutate(id)}
          onCreateVisit={(visitTargetId) => createVisit.mutate({ visitTargetId })}
          onScheduleDate={() => {
            setScheduleTarget({
              visitTargetId: contextMenu.item.visitTargetId,
              clientId: contextMenu.item.client.id,
              clientName: contextMenu.item.displayName || contextMenu.item.client.name,
            });
          }}
          onMarkSkipped={(id) => markSkipped.mutate({ clientId: id })}
        />
      )}

      {/* Schedule Visit Dialog */}
      <ScheduleVisitDialog
        open={!!scheduleTarget}
        onOpenChange={(open) => !open && setScheduleTarget(null)}
        visitTargetId={scheduleTarget?.visitTargetId ?? 0}
        clientId={scheduleTarget?.clientId ?? 0}
        clientName={scheduleTarget?.clientName}
      />

      {/* Task Detail Sheet */}
      <TaskDetailSheet
        task={selectedTask}
        open={!!selectedTask}
        onOpenChange={(open) => !open && setSelectedTask(null)}
        onUpdate={handleTaskUpdate}
        onComplete={handleTaskComplete}
        onCancel={handleTaskCancel}
        isUpdating={updateTask.isPending}
        isCompleting={completeTask.isPending}
        isCancelling={cancelTask.isPending}
      />
    </div>
  );
}
